import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { EditableContent } from '../components/EditableContent';
import { AdminBar } from '../components/AdminBar';
import { LanguageSelector } from '../components/LanguageSelector';
import { useContent } from '../hooks/useContent';
import { useTitle } from '../hooks/useTitle';

export function SolutionPage() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const { fetchContent } = useContent();
  const { setTitle } = useTitle();
  
  useEffect(() => {
    if (slug) {
      fetchContent(slug);
    }
  }, [fetchContent, slug]);

  const solution = slug === 'sales' ? t.solutions.sales 
                : slug === 'marketing' ? t.solutions.marketing 
                : t.solutions.bi;

  useEffect(() => {
    setTitle(solution.title);
  }, [setTitle, solution.title]);

  return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-16">
          <Link 
            to="/" 
            className="inline-flex items-center text-orange-500 hover:text-orange-600 mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />
            Retour
          </Link>
          
          <EditableContent
            pageId={slug || ''}
            contentKey="title"
            type="text"
            defaultContent={solution.title}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            tag="h1"
          />
          
          <EditableContent
            pageId={slug || ''}
            contentKey="description"
            type="text"
            defaultContent="Une solution complète pour optimiser votre business"
            className="text-xl text-gray-600 max-w-2xl mb-12"
            tag="p"
          />
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Fonctionnalités</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {solution.items.map((item: string, index: number) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-lg">
                <CheckCircle2 className="w-6 h-6 text-orange-500 flex-shrink-0" />
                <div>
                  <EditableContent
                    pageId={slug || ''}
                    contentKey={`features.${index}`}
                    type="text"
                    defaultContent={item}
                    className="text-gray-800"
                    tag="p"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-16">Résultats</h2>
          <div className="inline-block bg-white rounded-xl shadow-lg px-8 py-4">
            <EditableContent
              pageId={slug || ''}
              contentKey="highlight"
              type="text"
              defaultContent={solution.highlight}
              className="text-2xl font-bold text-orange-500"
              tag="p"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <EditableContent
            pageId={slug || ''}
            contentKey="cta.title"
            type="text"
            defaultContent="Prêt à transformer votre business ?"
            className="text-3xl font-bold mb-8"
            tag="h2"
          />
          <EditableContent
            pageId={slug || ''}
            contentKey="cta.subtitle"
            type="text"
            defaultContent="Réservez votre session stratégique gratuite de 30 minutes"
            className="text-xl mb-12"
            tag="p"
          />
          <Link 
            to="#"
            className="inline-flex items-center gap-2 bg-white text-orange-500 px-8 py-4 rounded-full 
                     font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all"
          >
            <EditableContent
              pageId={slug || ''}
              contentKey="cta.button"
              type="text"
              defaultContent="Réserver maintenant"
              className="inline"
              tag="span"
            />
            <ArrowLeft className="w-5 h-5 rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
}